import { useContext, useEffect, useState } from "react";
import { LayerInfo } from "src/common/managers/Types";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { SheetFunc } from "src/managers/SheetFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ExportData } from "src/managers/ExportData";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { GlassButton } from "./GlassButtons";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "./GlassInput";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { exportZip } from "src/managers/ExportZip";

type exportType = "xlsx" | "geojson" | "layerInfo" | "styleJson" | "shp" | "kml" | "gml" | "gpkg" | "dxf" | "dgn" | "dwg"
type callbackEvent = "cancel" | "accept"

type Props = {
  managedWindow: ManagedWindow
  layers?: LayerInfo | LayerInfo[]
  name?: string
  callBack: (evt: callbackEvent) => void
};

export function DownloadDialog(props: Props) {
  const { hasAccessToFeature } = useContext(ApplicationStateContext)
  const layers = Array.isArray(props.layers) ? props.layers : props.layers ? [props.layers] : []
  const [name, setName] = useState(props?.name ?? (layers.length > 1 ? "MapitExport" : layers?.[0]?.datasetname ?? ""))
  const [format, setFormat] = useState<exportType>("geojson")

  const exportTypes = [
    "xlsx",
    "shp",
    "kml",
    "gml",
    "gpkg",
    "geojson",
    "styleJson",
    // ...(hasAccessToFeature(Feature.Debugging) ? ["dgn", "dxf"] : []),
    ...(hasAccessToFeature(Feature.Debugging) ? ["layerInfo"] : []),
  ]

  useEffect(() => {
    setName(props.name ?? (layers.length > 1 ? "MapitExport" : layers?.[0]?.datasetname ?? ""))
    setFormat("geojson")
  }, [props.layers, props.name])

  async function Accept() {
    let allFiles: { name: string; blob: Blob }[] = []
    allFiles = (await Promise.all(layers.map(async (a) => {
      return await TransformSingleLayer({ ...a, export: format })
    }))).flat()
    console.log("Multiple files to export:", allFiles)
    if (allFiles.length === 1) {
      ExportData.downloadBlob(allFiles[0].name, allFiles[0].blob)
    } else if (allFiles.length > 1) {
      exportZip(allFiles, name)
    }
    props.callBack("accept")
  }

  function Cancel() {
    props.callBack("cancel")
  }

  return <AdvancedDragModal
    window={props.managedWindow}
    title={Localization.getText("Export")}
    variant="Info"
    PosDefault={{
      left: "50%",
      top: "20%",
      transform: "translateX(-50%)"
    }}
  >
    <ADModalBody>
      <GlassInputGroup
        autoFocus={true}
        onEscape={Cancel}
        onSubmit={Accept}
      >
        <GlassTextInputTransformer
          label={Localization.getText("Name")}
        >
          <GlassInputControl>
            <input type="text" value={name} onChange={(ev) => setName(ev.target.value)} />
            <select value={format} style={{ flex: "unset", width: "18ex", cursor: "pointer" }}
              onChange={((a) => setFormat(a.target.value as exportType || "geojson"))}
            >
              {exportTypes.map((a) => {
                return (
                  <option key={a} value={a}>.{a}</option>
                )
              })}
            </select>
          </GlassInputControl>
        </GlassTextInputTransformer>
      </GlassInputGroup>
    </ADModalBody>
    <ADModalFooter>
      <GlassButton onClick={Accept}>{Localization.getText("Export")}</GlassButton>
      <GlassButton onClick={Cancel}>{Localization.getText("Cancel")}</GlassButton>
    </ADModalFooter>
  </AdvancedDragModal>
}


async function TransformSingleLayer(layerInfo: (LayerInfo & { export: exportType })): Promise<{ name: string; blob: Blob }[]> {
  let blob: Blob | null = null
  let extension = ".xlsx"

  switch (layerInfo.export) {
    case "layerInfo":
      blob = new Blob([JSON.stringify(layerInfo)], { type: "application/json" })
      extension = ".layerinfo"
      return [{ name: (layerInfo.datasetname ?? "MapitDownload") + extension, blob }];
    case "styleJson":
      blob = new Blob([JSON.stringify({
        ...validGeoJson(layerInfo.geoJson)
        , "style": layerInfo.styling
      })], { type: "application/json" })
      extension = ".geojson"
      return [{ name: (layerInfo.datasetname ?? "MapitDownload") + extension, blob }];
    case "geojson":
      blob = new Blob([JSON.stringify(validGeoJson(layerInfo.geoJson))], { type: "application/json" })
      extension = ".geojson"
      return [{ name: (layerInfo.datasetname ?? "MapitDownload") + extension, blob }];
    case "shp":
    case "kml":
    case "gpkg":
    case "dxf":
    case "dgn":
      blob = new Blob([JSON.stringify(validGeoJson(layerInfo.geoJson))], { type: "application/json" })
      let formData = new FormData();
      let transformBaseUrl = SettingsManager.getSystemSetting("transformBaseUrl");
      formData.append("input", blob, (layerInfo.datasetname ?? "MapitDownload") + ".geojson")
      return fetch(transformBaseUrl + "/" + layerInfo.export, {
        method: "POST",
        body: formData
      }).then(async (response) => {
        if (response.ok) {
          return await response.json().then(async (data) => {
            if ("files" in data && Array.isArray(data.files)) {
              let transformed = await Promise.all(data.files?.map(async (file: string) =>
                await fetch(transformBaseUrl + "/" + file).then(async (response) => {
                  if (response.ok) {
                    return await response.blob().then((blob) => {
                      return { name: file.split("/").pop() ?? "MapitDownload", blob }
                    })
                  }
                  return null;
                })
              ) ?? []) as { name: string; blob: Blob }[];
              return transformed;
            }
          })
        }
        return []
      }) as Promise<{ name: string; blob: Blob }[]>
    case "xlsx": {
      const features = layerInfo.geoJson?.features ?? [];
      let sheet = SheetFunc.jsonToSheet(features.map((a) => geoJsonFeatureToExcelRow(a)));
      blob = SheetFunc.createBlob(SheetFunc.sheetToDownload(sheet));
      return [{ name: (layerInfo.datasetname ?? "MapitDownload") + extension, blob }];
    }
  }
  return [];
}

/** One spreadsheet row per feature: attribute columns only (no WKT — use GeoJSON export for geometry). */
function geoJsonFeatureToExcelRow(feature: { geometry?: { type?: string; coordinates?: number[] }; properties?: Record<string, unknown> }) {
  const properties = omitGeometryPropertyKeys(feature.properties ?? {});
  if (!feature.geometry?.type) {
    return properties;
  }
  if (feature.geometry.type === "Point") {
    const coords = feature.geometry.coordinates ?? [];
    return { ...properties, "Lat": coords[0], "Lng": coords[1] };
  }
  return properties;
}

const geometryPropertyKeys = new Set(["wkt", "geometri", "geometry", "geoshape"]);

function omitGeometryPropertyKeys(properties: Record<string, unknown>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!geometryPropertyKeys.has(key.toLowerCase())) {
      row[key] = value;
    }
  }
  return row;
}

// Currently only converts coords to numbers
function validGeoJson(geojson: any) {
  return GenerateGeomUtils.geoJsonCoordsTransformer(geojson, (coord) => {
    return coord.map((a) => Number(a)) as [number, number]
  })
}